/**
 * ランディング複製ダイアログ。
 * モード:
 *  - simple: そのまま複製
 *  - ai:     セクション構成は踏襲、テキスト/トーンを AI で再生成
 *            + 任意で薬事法 NG ワード置換
 */
import { Copy, Loader2, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useDuplicateLanding } from '@/hooks/useLandings';

type Mode = 'simple' | 'ai';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceLandingId: string | null;
  sourceTitle?: string;
}

export function LandingDuplicateDialog({
  open,
  onOpenChange,
  sourceLandingId,
  sourceTitle,
}: Props) {
  const [mode, setMode] = useState<Mode>('simple');
  const [newTitle, setNewTitle] = useState('');
  const [overallDirection, setOverallDirection] = useState('');
  const [keyPoint, setKeyPoint] = useState('');
  const [sanitizePharma, setSanitizePharma] = useState(false);
  const dup = useDuplicateLanding();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setMode('simple');
      setOverallDirection('');
      setKeyPoint('');
      setSanitizePharma(false);
      setNewTitle(sourceTitle ? `${sourceTitle} (コピー)` : '');
    }
  }, [open, sourceTitle]);

  const handleDuplicate = async () => {
    if (!sourceLandingId) return;
    if (!newTitle.trim()) {
      toast.error('ランディング名を入力してください');
      return;
    }
    try {
      const res = await dup.mutateAsync({
        sourceLandingId,
        newTitle: newTitle.trim(),
        mode,
        overallDirection:
          mode === 'ai' ? overallDirection.trim() || undefined : undefined,
        keyPoint: mode === 'ai' ? keyPoint.trim() || undefined : undefined,
        sanitizePharmaceutical: mode === 'ai' ? sanitizePharma : undefined,
      });
      // キーポイントの配置結果をトーストに併記
      let extra = '';
      const kp = res.keyPoint;
      if (kp?.provided) {
        if (!kp.applied) {
          extra = ' / キーポイントは配置先を判定できず未反映';
        } else if (kp.action === 'create' && kp.createdSection) {
          extra = ` / キーポイント: 新セクション「${kp.createdSection.title || kp.createdSection.type}」を追加`;
        } else if (kp.action === 'update' && kp.targets?.length) {
          extra = ` / キーポイント: ${kp.targets.join('、')} に反映`;
        }
      }
      toast.success(`複製完了: ${res.slug}${extra}`);
      onOpenChange(false);
      navigate(`/landings/${res.landingId}`);
    } catch (e) {
      toast.error(`複製失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && dup.isPending) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="size-4 text-[var(--accent)]" />
            ランディングを複製
          </DialogTitle>
          <DialogDescription>
            {sourceTitle
              ? `「${sourceTitle}」を複製します。`
              : 'このランディングを複製します。'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-xs">
          {/* モード選択 */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">複製モード</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('simple')}
                disabled={dup.isPending}
                className={`text-left p-3 rounded-md border transition-colors ${
                  mode === 'simple'
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                    : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Copy className="size-3.5" />
                  <span className="font-semibold">複製</span>
                </div>
                <div className="text-[10px] text-[var(--text-muted)] leading-relaxed">
                  内容・デザインを完全にコピー
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMode('ai')}
                disabled={dup.isPending}
                className={`text-left p-3 rounded-md border transition-colors ${
                  mode === 'ai'
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                    : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Sparkles className="size-3.5 text-[var(--accent)]" />
                  <span className="font-semibold">AI 複製</span>
                </div>
                <div className="text-[10px] text-[var(--text-muted)] leading-relaxed">
                  構成は踏襲、テキストを再生成
                </div>
              </button>
            </div>
          </div>

          {/* 新ランディング名 */}
          <div className="space-y-1.5">
            <Label htmlFor="dup-title">新しいランディング名</Label>
            <Input
              id="dup-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="例: TimeWaver Biz 2026"
              maxLength={80}
            />
          </div>

          {/* AI モード追加オプション */}
          {mode === 'ai' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="dup-overall">
                  01 全体の方向性{' '}
                  <span className="font-normal text-[var(--text-muted)]">(任意)</span>
                </Label>
                <Textarea
                  id="dup-overall"
                  value={overallDirection}
                  onChange={(e) => setOverallDirection(e.target.value)}
                  placeholder={
                    '例: ターゲットを 30-40 代の経営者に / トーンは落ち着いた信頼感 / デザインテーマ: ミニマル・モノクロ'
                  }
                  rows={3}
                />
                <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
                  ページ全体に効かせたい指示（ターゲット・トーン・テーマなど）。
                  <strong>全セクション</strong>に反映されます。
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dup-keypoint">
                  02 キーポイント{' '}
                  <span className="font-normal text-[var(--text-muted)]">(任意)</span>
                </Label>
                <Textarea
                  id="dup-keypoint"
                  value={keyPoint}
                  onChange={(e) => setKeyPoint(e.target.value)}
                  placeholder={
                    '例: 新製品「TimeWaver QI Module」を紹介。特徴は短時間測定・高精度・クラウド連携'
                  }
                  rows={3}
                />
                <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
                  一部分だけに載せたい具体情報（新製品・新サービスなど）。AI が
                  <strong>最適なセクションを自動判定</strong>
                  し、そこだけに反映、または専用セクションを新規追加します。全体には散りません。
                </p>
              </div>

              <label
                htmlFor="dup-sanitize"
                className="flex items-start gap-2 p-3 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] cursor-pointer"
              >
                <input
                  id="dup-sanitize"
                  type="checkbox"
                  checked={sanitizePharma}
                  onChange={(e) => setSanitizePharma(e.target.checked)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="text-xs font-semibold">薬事法チェック</div>
                  <div className="text-[10px] text-[var(--text-muted)] mt-1 leading-relaxed">
                    AI 生成結果をスキャンし、薬事法に抵触しやすい表現（治る・効能・即効・完治 等）を安全な代替表現に自動置換します。
                    <br />
                    <span className="text-[var(--accent-amber, #d97706)]">
                      ※ 完全準拠は保証しません。公開前に必ず人間レビューを推奨。
                    </span>
                  </div>
                </div>
              </label>
            </>
          )}

          {mode === 'ai' && (
            <p className="text-[10px] text-[var(--text-muted)] p-2 bg-[var(--bg-elevated)] border border-[var(--border)] rounded">
              💡 AI 複製は全セクションを並列で再生成（キーポイント指定時は配置先の判定も実施）するため、完了まで <strong>1〜3 分</strong> 程度かかる場合があります。
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={dup.isPending}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleDuplicate}
            disabled={dup.isPending || !sourceLandingId || !newTitle.trim()}
          >
            {dup.isPending ? (
              <>
                <Loader2 className="size-3.5 mr-1 animate-spin" />
                {mode === 'ai' ? 'AI 複製中...' : '複製中...'}
              </>
            ) : mode === 'ai' ? (
              <>
                <Sparkles className="size-3.5 mr-1" />
                AI で複製
              </>
            ) : (
              <>
                <Copy className="size-3.5 mr-1" />
                複製
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
